"use client";

import {
	homeSovEntries,
	homeTraffic,
} from "@marketing/shared/lib/bento-fixtures";
import { Badge } from "@ui/components/badge";
import { DeltaBadge } from "@ui/components/delta-badge";
import { cn } from "@ui/lib";
import { CHART_SERIES } from "@ui/lib/chart-palette";
import { useTranslations } from "next-intl";
import { useRevealed } from "./motion";

/**
 * Home hero proof tiles. Deliberately CSS/SVG-only (no Recharts): these sit
 * above the fold, so they must not pull chart bundles into the home page's
 * critical path. Sample data stays EN; chrome reads marketing.homeV2.widgets.
 */

/** Compact competitor share-of-voice rows with the brand's row pinned. */
export function SovListTile({ className }: { className?: string }) {
	const t = useTranslations("marketing.homeV2.widgets");
	const revealed = useRevealed();
	const max = Math.max(...homeSovEntries.map((e) => e.share), 1);
	return (
		<div className={className}>
			<p className="text-xs font-semibold text-muted-foreground">
				{t("sovTitle")}
			</p>
			<ul className="mt-2.5 space-y-2">
				{homeSovEntries.map((entry, i) => (
					<li key={entry.name}>
						<div className="flex items-baseline justify-between gap-2">
							<span className="flex min-w-0 items-center gap-1.5">
								<span className="w-4 shrink-0 text-right font-mono text-[11px] tabular-nums text-muted-foreground">
									{i + 1}
								</span>
								<span className="truncate text-sm font-medium">
									{entry.name}
								</span>
								{entry.isSelf ? (
									<Badge status="info">{t("youBadge")}</Badge>
								) : null}
							</span>
							<span className="shrink-0 font-mono text-sm font-semibold tabular-nums">
								{entry.share.toFixed(1)}%
							</span>
						</div>
						<div className="ml-[22px] mt-1 h-1.5 rounded-full bg-muted">
							<div
								className="h-full rounded-full motion-safe:transition-[width] motion-safe:duration-700 motion-safe:ease-out"
								style={{
									width: revealed
										? `${(entry.share / max) * 100}%`
										: "0%",
									backgroundColor: entry.isSelf
										? CHART_SERIES[0]
										: "var(--muted-foreground)",
									opacity: entry.isSelf ? 1 : 0.4,
									transitionDelay: `${i * 110}ms`,
								}}
							/>
						</div>
					</li>
				))}
			</ul>
		</div>
	);
}

/** Inline SVG mini trend line — no chart library, draws in via dash offset. */
function MiniTrend({ series, color }: { series: number[]; color: string }) {
	const revealed = useRevealed();
	const width = 96;
	const height = 30;
	const step = width / (series.length - 1);
	const points = series
		.map((v, i) => `${(i * step).toFixed(1)},${(height - (v / 100) * (height - 4) - 2).toFixed(1)}`)
		.join(" ");
	return (
		<svg
			viewBox={`0 0 ${width} ${height}`}
			className="h-[30px] w-24 shrink-0"
			aria-hidden
		>
			<polyline
				points={points}
				fill="none"
				stroke={color}
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
				pathLength={1}
				strokeDasharray={1}
				strokeDashoffset={revealed ? 0 : 1}
				className="motion-safe:transition-[stroke-dashoffset] motion-safe:duration-[1100ms] motion-safe:ease-out"
			/>
		</svg>
	);
}

/** AI traffic stat rows: agent visits + human clickthroughs from AI. */
export function TrafficTile({ className }: { className?: string }) {
	const t = useTranslations("marketing.homeV2.widgets");
	const rows = [
		{
			label: t("agentVisits"),
			data: homeTraffic.agents,
			color: CHART_SERIES[0],
		},
		{
			label: t("clickthroughs"),
			data: homeTraffic.clickthroughs,
			color: CHART_SERIES[3],
		},
	];
	return (
		<div className={cn("space-y-4", className)}>
			<p className="text-xs font-semibold text-muted-foreground">
				{t("trafficTitle")}
			</p>
			{rows.map((row) => (
				<div
					key={row.label}
					className="flex items-end justify-between gap-3"
				>
					<div className="min-w-0">
						<p className="truncate text-xs text-muted-foreground">
							{row.label}
						</p>
						<p className="mt-0.5 flex items-center gap-2">
							<span className="font-mono text-2xl font-extrabold tabular-nums">
								{row.data.total.toLocaleString()}
							</span>
							<DeltaBadge change={row.data.trend} />
						</p>
					</div>
					<MiniTrend series={row.data.series} color={row.color} />
				</div>
			))}
		</div>
	);
}
